import { CHANNEL_LABEL } from '../recall-builder/types';
import { periodDays, scaleByPeriod, type PeriodValue } from '../lib/period';
import { RECALL_CAMPAIGNS } from './mockData';
import { LIST_GOAL_LABEL, recallById, resultNoun } from './store';
import type { RcListRow, RcReportData, RcReportStep } from './types';

/**
 * A recall's own report (amendment 22 §4), now period-aware (spec 41 §4.1).
 *
 * Each report describes the recall it was opened from, and the **whole page
 * follows the period selector** — every number is what happened in the chosen
 * window, except *In this recall*, which is true right now. The lapsed-18 recall
 * carries the spec's exact per-range table; other recalls scale their 30-day mock.
 * The steps' Booked add up to the funnel's Booked on every range (rule 6).
 */
export function reportFor(id: string, value: PeriodValue = { key: '30d' }): RcReportData | undefined {
  const row = recallById(id);
  if (row === undefined) return undefined;
  return buildReport(row, value);
}

/** The four exact columns for lapsed-18 (§4.1). Keyed by the report presets. */
const LAPSED18: Record<'7d' | '30d' | '60d' | 'all', { reached: number; engaged: number; booked: number; attended: number; production: string }> = {
  '7d': { reached: 302, engaged: 96, booked: 5, attended: 4, production: '$744' },
  '30d': { reached: 1237, engaged: 408, booked: 21, attended: 18, production: '$3,348' },
  '60d': { reached: 2105, engaged: 702, booked: 38, attended: 33, production: '$6,138' },
  all: { reached: 4870, engaged: 1640, booked: 128, attended: 119, production: '$22,100' },
};

/** The lapsed-18 steps at 30 days — Booked sums to 21 (§4.1). */
const LAPSED18_STEPS_30 = [
  { channel: 'Text', day: 'day 0', sent: 823, reached: 741, booked: 9 },
  { channel: 'Email', day: 'day 6', sent: 506, reached: 455, booked: 5 },
  { channel: 'AI call', day: 'day 12', sent: 366, reached: 329, booked: 4 },
  { channel: 'Human call', day: 'day 18', sent: 286, reached: 257, booked: 3 },
];

/** Which exact column a value maps to; custom/other scales the 30-day column. */
function keyFor(value: PeriodValue): '7d' | '30d' | '60d' | 'all' {
  if (value.key === '7d' || value.key === '30d' || value.key === '60d' || value.key === 'all') return value.key;
  return '30d';
}

function moneyScale(base: number, value: PeriodValue): string {
  return `$${(scaleByPeriod(base, value)).toLocaleString('en-US')}`;
}

/** All recalls started 3 Mar 2026 in the mock — the custom-range floor. */
const STARTED = '2026-03-03';

function buildReport(row: RcListRow, value: PeriodValue): RcReportData {
  const base = RECALL_CAMPAIGNS.report;
  const inProgress = row.inProgress ?? 0; // right now — never scales
  const noun = resultNoun(row.goal);
  const rates = periodDays(value, STARTED) >= 7;

  if (row.state === 'draft') {
    return {
      ...base,
      name: row.name,
      state: 'draft',
      cycle: `Not started · goal: ${LIST_GOAL_LABEL[row.goal]}`,
      heroProduction: '—',
      heroSub: 'This recall has not run yet, so there is nothing to report.',
      funnel: [],
      steps: [],
      bases: [],
      conclusion: '',
    };
  }

  // The per-range figures: exact for lapsed-18, scaled from the 30-day mock otherwise.
  const isLapsed = row.id === 'lapsed-18';
  let reached: number;
  let engaged: number;
  let booked: number;
  let attended: number;
  let production: string;
  let steps30: { channel: string; day: string; sent: number; reached: number; booked: number }[];

  if (isLapsed) {
    const col = LAPSED18[keyFor(value)];
    const scaled = value.key === 'custom';
    reached = scaled ? scaleByPeriod(LAPSED18['30d'].reached, value, STARTED) : col.reached;
    engaged = scaled ? scaleByPeriod(LAPSED18['30d'].engaged, value, STARTED) : col.engaged;
    booked = scaled ? scaleByPeriod(LAPSED18['30d'].booked, value, STARTED) : col.booked;
    attended = scaled ? scaleByPeriod(LAPSED18['30d'].attended, value, STARTED) : col.attended;
    production = scaled ? moneyScale(3348, value) : col.production;
    steps30 = LAPSED18_STEPS_30;
  } else {
    const result30 = row.last30 ?? 0;
    booked = scaleByPeriod(result30, value, STARTED);
    reached = scaleByPeriod(Math.round(inProgress * 0.94), value, STARTED);
    engaged = scaleByPeriod(Math.round(inProgress * 0.31), value, STARTED);
    attended = Math.round(booked * 0.86);
    production = `$${(attended * 186).toLocaleString('en-US')}`;
    steps30 = row.steps.map((channel, i) => {
      const sent = Math.max(0, Math.round(inProgress / (i + 1.6)));
      return { channel: CHANNEL_LABEL[channel], day: `day ${String(i === 0 ? 0 : i * 6)}`, sent, reached: Math.round(sent * 0.9), booked: 0 };
    });
  }

  const pct = (n: number) => (inProgress === 0 ? '—' : `${String(Math.round((n / inProgress) * 100))}%`);
  const Noun = `${noun.charAt(0).toUpperCase()}${noun.slice(1)}`;

  // Steps scaled to the chosen window; the last step absorbs rounding so the
  // Booked column sums to the funnel's Booked (rule 6).
  const sentFactor = isLapsed ? scaleFactor(reached, LAPSED18['30d'].reached, value) : 1;
  const steps: RcReportStep[] = steps30.map((s, i) => {
    const sent = isLapsed ? Math.round(s.sent * sentFactor) : s.sent;
    const stepReached = isLapsed ? Math.round(s.reached * sentFactor) : s.reached;
    const base30Booked = steps30.reduce((a, b) => a + b.booked, 0) || 1;
    const stepBooked = Math.round((s.booked / base30Booked) * booked);
    return {
      n: String(i + 1),
      day: s.day,
      channel: s.channel,
      sent,
      reached: stepReached,
      booked: stepBooked,
      ofReached: stepReached === 0 ? '—' : `${(((stepBooked / stepReached) * 100)).toFixed(1)}%`,
      wait: i === steps30.length - 1 ? 'the last step — the recall ends here' : 'then waits before the next step',
    };
  });
  // Fix the last step so the column sums exactly to booked.
  const stepSum = steps.reduce((a, s) => a + s.booked, 0);
  const last = steps[steps.length - 1];
  if (last) last.booked = Math.max(0, last.booked + (booked - stepSum));
  const totalSent = steps.reduce((a, s) => a + s.sent, 0);

  return {
    ...base,
    name: row.name,
    state: row.state,
    cycle: `Running since ${row.since ?? '—'} · goal: ${LIST_GOAL_LABEL[row.goal]}`,
    heroProduction: production,
    heroSub: `${String(attended)} attended`,
    funnel: [
      { key: 'in', label: 'In this recall', value: inProgress, pct: '100%', rightNow: true, note: 'patients' },
      { key: 'reached', label: 'Reached', value: reached, pct: pct(reached), note: 'the message arrived' },
      { key: 'engaged', label: 'Engaged', value: engaged, pct: pct(engaged), emphasis: true, note: 'replied or opened' },
      { key: 'result', label: Noun, value: booked, pct: pct(booked) },
      { key: 'attended', label: 'Attended', value: attended, pct: pct(attended), emphasis: true, note: 'turned up' },
    ],
    steps,
    bases: [
      { label: 'Booking rate', value: rates ? `${(((booked / Math.max(1, reached)) * 100)).toFixed(1)}%` : '—', note: rates ? `${String(booked)} booked ÷ ${String(reached)} reached` : 'Shown for 7 days or more' },
      { label: 'Attendance', value: rates ? `${String(Math.round((attended / Math.max(1, booked)) * 100))}%` : '—', note: rates ? `${String(attended)} attended ÷ ${String(booked)} booked` : 'Shown for 7 days or more' },
      { label: 'Messages sent', value: totalSent.toLocaleString('en-US'), note: 'across every step' },
      { label: 'Production', value: production, note: `${String(attended)} visits × $186` },
    ],
    conclusion: '',
  };
}

/** Scale factor for sent/reached from the 30-day lapsed figures. */
function scaleFactor(rangeReached: number, base30Reached: number, value: PeriodValue): number {
  if (value.key === '30d') return 1;
  return rangeReached / Math.max(1, base30Reached);
}
